export type TouchGestureAction =
  | { type: "tap"; x: number; y: number }
  | { type: "pan"; deltaX: number; deltaY: number }
  | { type: "zoom"; factor: number; x: number; y: number };

interface TrackedPointer {
  x: number;
  y: number;
  startX: number;
  startY: number;
  moved: boolean;
}

const TAP_MOVEMENT_THRESHOLD = 10;

export class TouchGestureTracker {
  private readonly pointers = new Map<number, TrackedPointer>();
  private previousPinchDistance: number | undefined;

  pointerDown(id: number, x: number, y: number): void {
    this.pointers.set(id, { x, y, startX: x, startY: y, moved: false });
    if (this.pointers.size > 1) {
      for (const pointer of this.pointers.values()) pointer.moved = true;
      this.previousPinchDistance = this.distanceBetweenFirstTwoPointers();
    }
  }

  pointerMove(
    id: number,
    x: number,
    y: number,
  ): TouchGestureAction | undefined {
    const pointer = this.pointers.get(id);
    if (!pointer) return undefined;
    const deltaX = x - pointer.x;
    const deltaY = y - pointer.y;
    pointer.x = x;
    pointer.y = y;
    if (
      Math.hypot(x - pointer.startX, y - pointer.startY) >=
      TAP_MOVEMENT_THRESHOLD
    )
      pointer.moved = true;

    if (this.pointers.size > 1) {
      const points = [...this.pointers.values()].slice(0, 2);
      const [first, second] = points;
      if (!first || !second) return undefined;
      const distance = Math.hypot(first.x - second.x, first.y - second.y);
      const centerX = (first.x + second.x) / 2;
      const centerY = (first.y + second.y) / 2;
      const previousDistance = this.previousPinchDistance;
      this.previousPinchDistance = distance;
      if (!previousDistance || previousDistance <= 0 || distance <= 0)
        return undefined;
      const factor = distance / previousDistance;
      return Math.abs(factor - 1) < 0.01
        ? undefined
        : { type: "zoom", factor, x: centerX, y: centerY };
    }

    return pointer.moved && (deltaX !== 0 || deltaY !== 0)
      ? { type: "pan", deltaX, deltaY }
      : undefined;
  }

  pointerUp(id: number, x: number, y: number): TouchGestureAction | undefined {
    const pointer = this.pointers.get(id);
    if (!pointer) return undefined;
    pointer.x = x;
    pointer.y = y;
    if (
      Math.hypot(x - pointer.startX, y - pointer.startY) >=
      TAP_MOVEMENT_THRESHOLD
    )
      pointer.moved = true;
    const isTap = this.pointers.size === 1 && !pointer.moved;
    this.pointers.delete(id);
    this.previousPinchDistance =
      this.pointers.size > 1
        ? this.distanceBetweenFirstTwoPointers()
        : undefined;
    return isTap ? { type: "tap", x, y } : undefined;
  }

  pointerCancel(id: number): void {
    this.pointers.delete(id);
    this.previousPinchDistance =
      this.pointers.size > 1
        ? this.distanceBetweenFirstTwoPointers()
        : undefined;
  }

  reset(): void {
    this.pointers.clear();
    this.previousPinchDistance = undefined;
  }

  private distanceBetweenFirstTwoPointers(): number | undefined {
    const [first, second] = [...this.pointers.values()].slice(0, 2);
    return first && second
      ? Math.hypot(first.x - second.x, first.y - second.y)
      : undefined;
  }
}
